import React from 'react';
import './DeleteAccountContent.css';

interface DeleteAccountContentProps {
  deleteSuccess: boolean;
  isDeleting: boolean;
  deleteError: string | null;
  deleteConfirmText: string;
  deleteProgress: number;
  deleteStep: string;
  onDeleteConfirmTextChange: (value: string) => void;
  onDeleteAccount: () => void;
  onCancel: () => void;
  isMobile?: boolean;
}

const DeleteAccountContent: React.FC<DeleteAccountContentProps> = ({
  deleteSuccess,
  isDeleting,
  deleteError,
  deleteConfirmText,
  deleteProgress,
  deleteStep,
  onDeleteConfirmTextChange,
  onDeleteAccount,
  onCancel,
  isMobile = false
}) => {
  return (
    <div className={`delete-account-content ${isMobile ? 'mobile' : 'desktop'}`}>
      {deleteSuccess ? (
        <div className="delete-success">
          <div className="delete-success-content">
            <h3>✅ Account Deleted Successfully</h3>
            <p>Your ZoningPal account and all associated data have been permanently removed from our servers. You will be redirected to the home page momentarily.</p>
          </div>
        </div>
      ) : isDeleting ? (
        <div className="delete-progress">
          <div className="delete-progress-content">
            <div className="delete-progress-header">
              <h3>Deleting Your Account</h3>
              <p className="delete-progress-step">{deleteStep}</p>
            </div>
            
            <div className="delete-progress-bar">
              <div className="delete-progress-track">
                <div 
                  className="delete-progress-fill"
                  style={{ width: `${deleteProgress}%` }}
                />
              </div>
              <span className="delete-progress-text">{deleteProgress}%</span>
            </div>
            
            <div className="delete-progress-warning">
              <p><strong>Please keep this window open until completion.</strong></p>
              <p>This process cannot be interrupted once started.</p>
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="delete-warning">
            <div className="delete-warning-content">
              <h3>Permanent Account Deletion</h3>
              <p>This action will permanently remove your ZoningPal account and cannot be reversed. All of the following will be deleted:</p>
              <ul>
                <li>Saved reports and project analysis</li>
                <li>Account settings and preferences</li>
                <li>Subscription and billing information</li>
                <li>Associated files and documents</li>
              </ul>
            </div>
          </div>

          <div className="delete-confirmation">
            <label htmlFor="delete-confirm" className="delete-confirm-label">
              Please type <strong>DELETE MY ACCOUNT</strong> to confirm:
            </label>
            <input
              id="delete-confirm"
              type="text"
              className={`delete-confirm-input ${deleteError ? 'error' : ''}`}
              value={deleteConfirmText}
              onChange={(e) => onDeleteConfirmTextChange(e.target.value)}
              placeholder="DELETE MY ACCOUNT"
              disabled={isDeleting}
            />
            {deleteError && (
              <div className="delete-error">{deleteError}</div>
            )}
          </div>

          <div className="delete-actions">
            <button 
              className="delete-cancel-btn"
              onClick={onCancel}
              disabled={isDeleting}
            >
              Cancel
            </button>
            <button 
              className={`delete-confirm-btn ${isDeleting ? 'loading' : ''}`}
              onClick={onDeleteAccount}
              disabled={isDeleting || deleteConfirmText !== 'DELETE MY ACCOUNT'}
            >
              {isDeleting ? 'Deleting Account...' : 'Delete Account'}
            </button>
          </div>
        </>
      )}

      {deleteSuccess && (
        <div className="delete-success-footer">
          <p>Redirecting you to the home page...</p>
        </div>
      )}
    </div>
  );
};

export default DeleteAccountContent; 